import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, IconButton, Chip, CircularProgress,
  Tooltip, Slide, List, ListItemButton, ListItemText,
  useTheme,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Refresh as ClearIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Build as ToolIcon,
} from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import { assistantChat, getConversations, getConversationById, deleteConversation } from '../../api/agents';

const PAGE_PROMPTS = {
  '/dashboard':         ['What needs my attention today?', "This week's KPIs?", 'Critical stockouts?'],
  '/inventory':         ['Items below reorder point?', 'Show slow-moving stock', 'Cross-plant imbalances?'],
  '/suppliers':         ['Suppliers with expiring docs?', 'Lowest-rated suppliers?', 'Compliance issues?'],
  '/customers':         ['Any at-risk customers?', 'Who hasn\'t ordered in 60 days?'],
  '/rfqs':              ['Which RFQs need evaluation?', 'Overdue bid responses?', 'Show open RFQs'],
  '/purchase-orders':   ['Which POs are overdue?', 'What\'s pending approval?', 'PO spend this month?'],
  '/contracts':         ['Contracts expiring soon?', 'Show active contracts'],
  '/quotations':        ['Quotes expiring this week?', 'Which quotes need follow-up?'],
  '/sales-orders':      ['Sales this week vs last week?', 'Pending fulfilment?'],
  '/goods-receipts':    ['Unreconciled GRs?', 'GRs without PO match?'],
  '/approvals':         ['What\'s waiting for my approval?', 'How long have approvals been stuck?'],
  '/reports':           ['How did we do this week?', 'Any KPI anomalies?'],
};
const DEFAULT_PROMPTS = ['What needs attention today?', 'Show pending approvals', 'Critical stock levels?'];

function getPrompts(pathname) {
  for (const [path, prompts] of Object.entries(PAGE_PROMPTS)) {
    if (pathname.startsWith(path)) return prompts;
  }
  return DEFAULT_PROMPTS;
}

const WELCOME = `Hi! I'm KoorAI Assistant.\n\nI can answer questions about your procurement, inventory, sales, suppliers, and more — all queries run on your server and your data never leaves your system.\n\nWhat would you like to know?`;

const WELCOME_FAQ = `Hi! I'm KoorAI Assistant.\n\nI can help with general questions about KoorAI ONE features, SCM concepts, and how things work. Your conversations are private and stay on your server.\n\nWhat would you like to know?`;

// ─── Static FAQ Engine (zero API calls) ────────────────────────────────────
const FAQ_DB = [
  {
    keys: ['purchase order', ' po ', 'create po', 'approve po', 'po status', 'po workflow'],
    answer: `**Purchase Orders (PO)**

A Purchase Order is a formal document sent to a supplier to authorise a purchase.

**Workflow:** Draft → Submitted → Approved → Sent to Supplier → Goods Received → Invoice Matched → Paid

**In KoorAI ONE:**
- Go to **Purchase Orders** in the sidebar
- Click **New PO** to create one — select supplier, add line items, set delivery date
- Admins approve POs above the configured threshold
- Once approved, the PO is sent and tracked until goods are received`,
  },
  {
    keys: ['rfq', 'request for quotation', 'quotation request', 'bid', 'tender'],
    answer: `**RFQ — Request for Quotation**

An RFQ is sent to multiple suppliers asking for their best price on items you need.

**Workflow:** Create RFQ → Send to Suppliers → Collect Bids → Evaluate → Award to Best Supplier → Convert to PO

**In KoorAI ONE:**
- Go to **RFQs** in the sidebar
- Add items and select which suppliers to invite
- Suppliers submit their quotes through the Supplier Portal
- Use the comparison view to award the best bid`,
  },
  {
    keys: ['inventory', 'stock', 'reorder', 'reorder point', 'safety stock', 'stockout', 'warehouse'],
    answer: `**Inventory Management**

KoorAI ONE tracks stock across multiple plants/warehouses.

**Key concepts:**
- **Reorder Point** — when stock falls below this level, a replenishment PO is triggered
- **Safety Stock** — buffer stock kept to handle demand spikes
- **Goods Issue** — stock consumed or transferred out
- **Goods Receipt** — stock received from a supplier

**In KoorAI ONE:** Go to **Inventory** to view stock levels, movement history, and items below reorder point.`,
  },
  {
    keys: ['supplier', 'vendor', 'supplier portal', 'supplier compliance', 'supplier rating', 'supplier document'],
    answer: `**Supplier Management**

Suppliers are your external partners who provide goods or services.

**Key features in KoorAI ONE:**
- **Supplier Portal** — suppliers log in at /supplier-portal to view POs and submit quotes
- **Compliance docs** — upload trade licences, certifications; get alerts when they expire
- **Rating** — performance scores based on delivery, quality, and responsiveness
- **Approved Supplier List** — only approved suppliers can be added to POs`,
  },
  {
    keys: ['sales order', 'so', 'customer order', 'sales'],
    answer: `**Sales Orders (SO)**

A Sales Order records a customer's request to purchase goods or services from your company.

**Workflow:** Quotation → Sales Order → Fulfilment → Delivery → Invoice → Payment

**In KoorAI ONE:** Go to **Sales Orders** in the sidebar. You can track order status, fulfilment progress, and link to customer invoices.`,
  },
  {
    keys: ['goods receipt', 'grn', 'gr ', 'receive goods', 'goods received'],
    answer: `**Goods Receipts (GR)**

A Goods Receipt records the physical receipt of goods from a supplier against a PO.

**Process:**
1. Goods arrive at your warehouse
2. Create a GR referencing the PO
3. Record actual quantities received (may differ from ordered)
4. Stock levels update automatically
5. GR is matched against the supplier invoice for 3-way matching

**In KoorAI ONE:** Go to **Goods Receipts** to create and manage receipt records.`,
  },
  {
    keys: ['approval', 'approve', 'pending approval', 'approval workflow', 'authorisation'],
    answer: `**Approvals**

KoorAI ONE has a configurable approval workflow for POs, RFQs, and other transactions.

**How it works:**
- Transactions above a threshold automatically go for approval
- Approvers get notified and can approve/reject with a comment
- You can see all pending approvals in the **Approvals** section

**Tip:** Admins can configure approval thresholds and approver chains in Settings.`,
  },
  {
    keys: ['contract', 'agreement', 'framework agreement', 'contract expiry'],
    answer: `**Contracts**

Contracts in KoorAI ONE record formal agreements with suppliers or customers.

**Features:**
- Track start date, end date, value, and terms
- Automatic alerts before contract expiry
- Link POs or SOs to an active contract
- Upload the signed contract document

Go to **Contracts** in the sidebar to manage all agreements.`,
  },
  {
    keys: ['quotation', 'sales quotation', 'quote', 'proforma'],
    answer: `**Sales Quotations**

A quotation is a formal price offer sent to a prospective customer before a Sales Order is raised.

**Workflow:** Enquiry → Quotation → Customer Accepts → Convert to Sales Order

Quotations have an expiry date. Go to **Quotations** in the sidebar to manage them.`,
  },
  {
    keys: ['role', 'permission', 'access', 'user role', 'admin', 'buyer', 'store keeper'],
    answer: `**User Roles & Permissions**

KoorAI ONE has role-based access control:

| Role | Can do |
|---|---|
| **SuperAdmin** | Platform-wide settings, all orgs |
| **Admin** | All features within their org |
| **Purchase Manager** | Manage POs, RFQs, suppliers |
| **Buyer** | Create POs and RFQs |
| **Inventory Manager** | Manage stock, GRs, plants |
| **Store Keeper** | Record goods receipts/issues |
| **Sales** | Manage quotations and sales orders |

Roles are assigned by your Admin in the User Management section.`,
  },
  {
    keys: ['report', 'kpi', 'dashboard', 'analytics', 'spend analysis', 'monthly report'],
    answer: `**Reports & KPIs**

Go to **Reports** in the sidebar for:
- **Spend Analysis** — purchase spend by supplier, category, period
- **Inventory Reports** — stock valuation, movement, ageing
- **Supplier Performance** — on-time delivery, quality scores
- **Sales Reports** — revenue, order fulfilment rate

The **Dashboard** shows your key KPIs at a glance including pending approvals, low-stock alerts, and overdue POs.`,
  },
  {
    keys: ['plant', 'multi-plant', 'location', 'warehouse', 'branch'],
    answer: `**Plants & Warehouses**

KoorAI ONE supports multiple plants (locations/warehouses) within one organisation.

- Each plant has its own stock levels
- You can transfer stock between plants
- POs can be raised for a specific plant
- Reports can be filtered by plant

Plants are configured by your Admin in Settings → Plants.`,
  },
  {
    keys: ['three way match', '3 way', 'invoice match', 'po gr invoice'],
    answer: `**3-Way Matching**

3-way matching verifies that a supplier invoice is valid by comparing three documents:

1. **Purchase Order** — what you ordered and agreed to pay
2. **Goods Receipt** — what was actually delivered
3. **Supplier Invoice** — what the supplier is billing you

All three must match (within tolerance) before an invoice is approved for payment. This prevents overpayment and fraud.`,
  },
  {
    keys: ['what is erp', 'what is scm', 'what is koorai one', 'erp system', 'supply chain'],
    answer: `**What is KoorAI ONE?**

KoorAI ONE is a Supply Chain Management (SCM) platform that helps businesses manage:

- **Procurement** — buying goods and services from suppliers
- **Inventory** — tracking stock across warehouses
- **Sales** — managing customer orders and quotations
- **Supplier relationships** — compliance, ratings, portal access
- **Finance integration** — PO-to-invoice matching, spend tracking

It's built for manufacturing, trading, and distribution companies that need visibility across their entire supply chain.`,
  },
];

const FAQ_FALLBACK = `I'm not sure about that specific topic. Here are some things I can help with:

- **Purchase Orders** — creating, approving, tracking
- **RFQs** — requesting quotes from suppliers
- **Inventory** — stock levels, reorder points, goods receipts
- **Suppliers** — portal, compliance, ratings
- **Sales Orders** — customer orders, quotations
- **Approvals** — workflow, pending items
- **Reports & KPIs** — spend analysis, dashboards
- **User roles** — permissions and access levels

Try asking about any of these topics, or contact your administrator to enable full AI agents for live data queries.`;

function getFaqResponse(message) {
  const q = message.toLowerCase();
  for (const faq of FAQ_DB) {
    if (faq.keys.some(k => q.includes(k.trim()))) return faq.answer;
  }
  return FAQ_FALLBACK;
}

function ToolCallBadge({ toolCalls }) {
  if (!toolCalls?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.5 }}>
      {toolCalls.map((t, i) => (
        <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.6, py: 0.15, borderRadius: 0.75, bgcolor: t.success ? '#e8f5e9' : '#fce4ec', border: `1px solid ${t.success ? '#a5d6a7' : '#ef9a9a'}` }}>
          <ToolIcon sx={{ fontSize: 8, color: t.success ? '#388e3c' : '#c62828' }} />
          <Typography sx={{ fontSize: '0.625rem', color: t.success ? '#388e3c' : '#c62828', fontWeight: 500, lineHeight: 1, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.tool.replace('query_', '').replace(/_/g, ' ')}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function KoorAIAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const agentMain = theme.palette.agentPurple.main;
  const agentDark = theme.palette.agentPurple.dark;

  const faqMode = user?.agentsEnabled === false;

  const [open, setOpen]               = useState(false);
  const [view, setView]               = useState('chat');   // 'chat' | 'history'
  const [minimised, setMinimised]     = useState(false);
  const [messages, setMessages]       = useState([{ role: 'assistant', text: faqMode ? WELCOME_FAQ : WELCOME }]);
  const [input, setInput]             = useState('');
  const [sending, setSending]         = useState(false);
  const [unread, setUnread]           = useState(0);
  const [conversationId, setConvId]   = useState(null);
  const [conversations, setConvList]  = useState([]);
  const [loadingHistory, setLoadingH] = useState(false);

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const companyId = user?.companyId || user?.activeCompanyId;
  const pathname  = location.pathname;
  const hidden    = !companyId || ['/', '/login', '/register', '/supplier-portal'].some(p => pathname === p || pathname.startsWith('/supplier-portal'));
  const prompts   = getPrompts(pathname);

  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  useEffect(() => {
    if (open && !minimised) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, minimised]);

  const loadHistory = useCallback(async () => {
    if (!companyId) return;
    setLoadingH(true);
    try {
      const r = await getConversations(companyId);
      setConvList(r.data.conversations || []);
    } catch {}
    finally { setLoadingH(false); }
  }, [companyId]);

  const openHistory = () => { setView('history'); loadHistory(); };

  const loadConversation = async (id) => {
    try {
      const r = await getConversationById(companyId, id);
      const convo = r.data.conversation;
      if (!convo) return;
      const msgs = (convo.messages || []).map(m => ({
        role: m.role, text: m.content, toolCalls: m.toolCalls, provider: m.provider,
      }));
      setMessages(msgs.length ? msgs : [{ role: 'assistant', text: WELCOME }]);
      setConvId(id);
      setView('chat');
    } catch {}
  };

  const deleteConvo = async (id, e) => {
    e.stopPropagation();
    try { await deleteConversation(companyId, id); setConvList(l => l.filter(c => c._id !== id)); }
    catch {}
  };

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setSending(true);

    if (faqMode) {
      // Static FAQ — zero API calls, instant response with small UX delay
      setTimeout(() => {
        setMessages(m => [...m, { role: 'assistant', text: getFaqResponse(msg) }]);
        if (!open || minimised) setUnread(n => n + 1);
        setSending(false);
      }, 350);
      return;
    }

    try {
      const r = await assistantChat(companyId, msg, conversationId, pathname);
      const d = r.data;
      setMessages(m => [...m, { role: 'assistant', text: d.reply || 'No response.', toolCalls: d.toolCalls, provider: d.provider }]);
      if (d.conversationId) setConvId(d.conversationId);
      if (!open || minimised) setUnread(n => n + 1);
    } catch (e) {
      const errMsg = e.response?.data?.message || 'Something went wrong. Please try again.';
      setMessages(m => [...m, { role: 'assistant', text: `⚠️ ${errMsg}`, error: true }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, faqMode, companyId, pathname, open, minimised, conversationId]);

  // Lets other pages (e.g. the Dashboard's Ask-AI bar) drive this widget
  // instead of building a second chat UI — dispatch
  // window.dispatchEvent(new CustomEvent('koorai:ask', { detail: { message } }))
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message;
      if (!msg) return;
      setOpen(true);
      setMinimised(false);
      setView('chat');
      send(msg);
    };
    window.addEventListener('koorai:ask', handler);
    return () => window.removeEventListener('koorai:ask', handler);
  }, [send]);

  const newChat = () => { setMessages([{ role: 'assistant', text: faqMode ? WELCOME_FAQ : WELCOME }]); setConvId(null); setInput(''); setView('chat'); };
  const toggleOpen = () => { setOpen(o => !o); setMinimised(false); setView('chat'); };

  if (hidden) return null;

  return (
    <>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper elevation={8} sx={{
          position: 'fixed', bottom: 88, right: 24, zIndex: 1300,
          width: { xs: 'calc(100vw - 48px)', sm: 390 },
          height: minimised ? 56 : 540,
          display: 'flex', flexDirection: 'column',
          borderRadius: 3, overflow: 'hidden',
          border: '1px solid rgba(156,39,176,0.2)',
          transition: 'height 250ms ease',
          boxShadow: '0 8px 32px rgba(156,39,176,0.18)',
        }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, background: `linear-gradient(135deg, ${agentDark}, ${agentMain})`, cursor: 'pointer', flexShrink: 0 }} onClick={() => setMinimised(m => !m)}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SmartToyIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.2 }}>KoorAI Assistant</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.625rem' }}>
                {sending ? 'Thinking…' : 'Your data never leaves your server'}
              </Typography>
            </Box>
            {view === 'chat' && !minimised && !faqMode && (
              <Tooltip title="Chat history"><IconButton size="small" onClick={e => { e.stopPropagation(); openHistory(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {view === 'chat' && !minimised && (
              <Tooltip title="New chat"><IconButton size="small" onClick={e => { e.stopPropagation(); newChat(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}><ClearIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {view === 'history' && !minimised && (
              <Tooltip title="Back to chat"><IconButton size="small" onClick={e => { e.stopPropagation(); setView('chat'); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}><BackIcon fontSize="small" /></IconButton></Tooltip>
            )}
            <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(false); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {!minimised && view === 'history' && (
            <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fafafa' }}>
              <Box sx={{ px: 2, py: 1 }}><Typography variant="caption" color="text.secondary" fontWeight={600}>RECENT CONVERSATIONS</Typography></Box>
              {loadingHistory && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>}
              {!loadingHistory && conversations.length === 0 && <Box sx={{ px: 2, py: 3, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">No conversations yet</Typography></Box>}
              <List dense disablePadding>
                {conversations.map(c => (
                  <ListItemButton key={c._id} onClick={() => loadConversation(c._id)} sx={{ px: 2, py: 0.75, '&:hover .del-btn': { opacity: 1 } }}>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: '0.813rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || 'Chat'}</Typography>}
                      secondary={<Typography sx={{ fontSize: '0.625rem', color: 'text.disabled' }}>{new Date(c.updatedAt).toLocaleDateString('en-IN')} · {(c.tokenUsage || 0).toLocaleString()} tokens</Typography>}
                    />
                    <IconButton size="small" className="del-btn" onClick={e => deleteConvo(c._id, e)} sx={{ opacity: 0, transition: '0.15s', color: 'text.disabled', '&:hover': { color: 'error.main' } }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          {!minimised && view === 'chat' && (
            <>
              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#fafafa' }}>
                {messages.map((m, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 0.75 }}>
                    {m.role === 'assistant' && (
                      <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: `${agentMain}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-start', mt: 0.5 }}>
                        <SmartToyIcon sx={{ fontSize: 14, color: agentMain }} />
                      </Box>
                    )}
                    <Box sx={{ maxWidth: '82%' }}>
                      <Paper elevation={0} sx={{
                        px: 1.5, py: 1,
                        bgcolor: m.role === 'user' ? agentMain : m.error ? '#fff3e0' : '#fff',
                        color: m.role === 'user' ? '#fff' : 'text.primary',
                        borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        border: m.role === 'user' ? 'none' : '1px solid rgba(0,0,0,0.06)',
                      }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.813rem', lineHeight: 1.55 }}>{m.text}</Typography>
                      </Paper>
                      {m.role === 'assistant' && m.toolCalls?.length > 0 && (
                        <Box sx={{ mt: 0.3 }}><ToolCallBadge toolCalls={m.toolCalls} /></Box>
                      )}
                    </Box>
                  </Box>
                ))}
                {sending && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: `${agentMain}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><SmartToyIcon sx={{ fontSize: 14, color: agentMain }} /></Box>
                    <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', px: 1.5, py: 0.75, bgcolor: '#fff', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)' }}>
                      {[0, 1, 2].map(i => <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: agentMain, animation: 'pulse 1.2s infinite', animationDelay: `${i * 0.2}s`, '@keyframes pulse': { '0%,100%': { opacity: 0.3 }, '50%': { opacity: 1 } } }} />)}
                    </Box>
                  </Box>
                )}
                <div ref={bottomRef} />
              </Box>

              {/* Quick prompts */}
              {messages.length <= 1 && (
                <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', bgcolor: '#fafafa' }}>
                  {(faqMode ? ['What is a Purchase Order?', 'How do RFQs work?', 'What is inventory reorder point?'] : prompts).map((p, i) => (
                    <Chip key={i} label={p} size="small" onClick={() => send(p)}
                      sx={{ fontSize: '0.688rem', cursor: 'pointer', bgcolor: `${agentMain}12`, color: agentDark, border: `1px solid ${agentMain}30`, '&:hover': { bgcolor: `${agentMain}20` } }} />
                  ))}
                </Box>
              )}

              {/* Input */}
              <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 0.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff' }}>
                <TextField
                  inputRef={inputRef} fullWidth size="small" placeholder="Ask anything…"
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                  disabled={sending} multiline maxRows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '0.813rem' } }}
                />
                <IconButton onClick={() => send()} disabled={!input.trim() || sending}
                  sx={{ bgcolor: agentMain, color: '#fff', borderRadius: 2, width: 38, height: 38, flexShrink: 0, '&:hover': { bgcolor: agentDark }, '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' } }}>
                  {sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      </Slide>

      {/* FAB */}
      <Tooltip title="KoorAI Assistant" placement="left">
        <Box onClick={toggleOpen} sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
          width: 56, height: 56, borderRadius: '50%',
          background: open ? 'linear-gradient(135deg, #6a1b9a, #9c27b0)' : 'linear-gradient(135deg, #9c27b0, #ab47bc)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 4px 20px rgba(156,39,176,0.45)',
          transition: 'all 250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': { transform: 'scale(1.08)', boxShadow: '0 6px 24px rgba(156,39,176,0.55)' },
          '&:active': { transform: 'scale(0.96)' },
        }}>
          {open ? <CloseIcon sx={{ color: '#fff', fontSize: 22 }} /> : <SmartToyIcon sx={{ color: '#fff', fontSize: 24 }} />}
          {unread > 0 && !open && (
            <Box sx={{ position: 'absolute', top: -2, right: -2, width: 18, height: 18, borderRadius: '50%', bgcolor: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.625rem', fontWeight: 700, lineHeight: 1 }}>{unread}</Typography>
            </Box>
          )}
          {!open && (
            <Box sx={{ position: 'absolute', width: '100%', height: '100%', borderRadius: '50%', border: '2px solid rgba(156,39,176,0.4)', animation: 'ripple 2s ease-out infinite', '@keyframes ripple': { '0%': { transform: 'scale(1)', opacity: 0.8 }, '100%': { transform: 'scale(1.6)', opacity: 0 } } }} />
          )}
        </Box>
      </Tooltip>
    </>
  );
}
